import React, { PropsWithChildren, useEffect, useRef, useState } from "react"
import AppleIcon from "@/components/icons/svg/Apple"
import * as Functions from "@/firebase/clientApp"
import { useUser } from "@/context/UserContext"
import { useInterval } from "usehooks-ts"
import * as rdd from "react-device-detect"
import Bugsnag from "@bugsnag/js"
import { usePathname } from "next/navigation"
import { UnsupportedBrowserMessage } from "@/components/UnsupportedBrowserCheck"
import * as Styles from "@/components/Styles"
import { SignInAndPayHeader } from "@/components/AuthenticateSlideContent"
import { validateLoginEmailAction } from "@/app/actions"
import { useFirebase } from "@/firebase/FirebaseProvider"
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCustomToken,
  signInWithPopup,
  updateEmail,
} from "@firebase/auth"
import AuthGoogleIcon from "@/components/icons/svg/AuthGoogleIcon"
import { useIsUnsupportedBrowser } from "@/support/unsupported-browser"
import AuthLoadingSpinner from "@/components/icons/svg/AuthLoadingSpinner"
import clog from "@/util/clog"

/**
 * Type Definitions
 */

// Define types for our state
interface AuthFlowError {
  message: string
  source?: string
}

interface EmailValidationResult {
  isValid: boolean
  suggestion?: string
  email?: string
}

interface RequestLoginEmailResult {
  success: boolean
  nonce?: string
  code?: string
}

// Screen types
type ScreenType = "emailInput" | "emailCode" | "signedIn"

// For TypeScript to recognize AppleID from window
interface WindowWithAppleID extends Window {
  AppleID?: {
    auth: {
      signIn: () => Promise<void>
      renderButton: () => void
    }
  }
}

/**
 * Reusable Components
 */

// Error message component
function ErrorMessage({ message }: { message?: string }) {
  return (
    <p
      data-testid={"error-message"}
      className={"text-center text-sm text-red-400"}
    >
      {message ?? "Unknown Error"}
    </p>
  )
}

type OnInputInputCompleteValue =
  | { type: "emailValid"; email: string; nonce: string }
  | { type: "signedIn" }

/**
 * Email Input Screen Component
 * Displays the email input form and social sign-in options
 */
interface EmailInputScreenProps {
  onComplete: (result: OnInputInputCompleteValue) => void
  isSignInWithGoogleVisible: boolean
  isSignInWithAppleVisible: boolean
  appleSignInButtonRef: React.RefObject<HTMLButtonElement>
  appleSignInButtonOnClick: () => void
}

function EmailInputScreen({
  onComplete,
  isSignInWithGoogleVisible,
  isSignInWithAppleVisible,
  appleSignInButtonRef,
  appleSignInButtonOnClick,
}: EmailInputScreenProps) {
  const { auth, ...functions } = useFirebase()
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<AuthFlowError | undefined>(undefined)
  const [emailValidationResult, setEmailValidationResult] = useState<
    EmailValidationResult | undefined
  >(undefined)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false)

  // Email validation regex
  const validateEmailFormat = (email: string): boolean => {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  }

  // Handle errors consistently
  const handleError = (message: string, source?: string) => {
    const err = new Error(message)
    console.error({ message, source, error: err })
    Bugsnag.notify(err)
    const errorObj = { message, source }
    setError(errorObj)
    setIsPending(false)
  }

  // Reset error state
  const clearError = () => {
    setError(undefined)
  }

  // Email validation
  const validateEmail = async (email: string) => {
    setIsPending(true)
    clearError()

    try {
      const result = await validateLoginEmailAction({ email })
      setEmailValidationResult(result.validation)
      setIsPending(false)

      if (!result.validation.isValid) {
        // Email is invalid, show error
        return
      }

      if (result.validation.suggestion) {
        // Email has a typo suggestion, show suggestion
        return
      }

      // Email is valid, proceed to request login
      await requestLoginEmail(email)
    } catch (error) {
      handleError((error as Error).message, "validateEmail")
    }
  }

  // Request login email
  const requestLoginEmail = async (email: string) => {
    setIsPending(true)
    clearError()

    try {
      const result = await Functions.auth_requestLoginEmail(functions, {
        email,
      })

      if (result.success && result.nonce) {
        // Success, notify parent to proceed to code input
        setIsPending(false)
        onComplete({ type: "emailValid", email, nonce: result.nonce })
      } else if (!result.success && result.code === "NOT_FOUND") {
        clog("User not found, sign in anonymously")
        const result = await signInAnonymously(auth)
        clog(`Anonymous user signed in, updating email to ${email}`, result)
        await updateEmail(result.user, email)
        clog("User email updated, syncing profile")
        await Functions.users_syncProfile(functions)
        clog("Profile synced")
        onComplete({ type: "signedIn" })
      } else {
        handleError("Failed to request login email", "requestLoginEmail")
      }
    } catch (error) {
      handleError((error as Error).message, "requestLoginEmail")
    }
  }

  // Handle email submission
  const handleSubmitEmail = async (email: string) => {
    // Check if we're resubmitting the same email with a typo
    if (
      emailValidationResult?.email === email &&
      typeof emailValidationResult?.suggestion === "string"
    ) {
      // Skip validation and go straight to request login
      await requestLoginEmail(email)
    } else {
      // Validate the email first
      await validateEmail(email)
    }
  }

  // Get email validation results for UI
  const emailTypoCorrectionSuggestion = emailValidationResult?.suggestion
  const isEmailInvalid = emailValidationResult?.isValid === false

  // Google sign in button click handler
  const googleSignInButtonOnClick = async () => {
    if (isGoogleAuthLoading) {
      return
    }

    try {
      setIsGoogleAuthLoading(true)
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      // The signed-in user info is handled by the onAuthStateChanged listener
      // If we get here, the sign-in was successful
      onComplete({ type: "signedIn" })
    } catch (error) {
      console.error("Google sign in error:", error)
      Bugsnag.notify(new Error("Google sign in error", { cause: error }))
      handleError("Failed to sign in with Google", "googleSignIn")
    } finally {
      setIsGoogleAuthLoading(false)
    }
  }

  return (
    <>
      <div className={`${Styles.brandHeadingTextClassName} mb-[10px]`}>
        Enter email
      </div>
      <form
        className={"flex w-full flex-col gap-[15px]"}
        noValidate={true}
        onSubmit={async (e) => {
          e.preventDefault()

          if (isEmailValid) {
            const emailInput = document.getElementById(
              "email",
            ) as HTMLInputElement
            await handleSubmitEmail(emailInput.value)
          }
        }}
      >
        <div className={"flex flex-col gap-1.5"}>
          <input
            ref={emailInputRef}
            id={"email"}
            name={"email"}
            type={"email"}
            autoComplete={"email"}
            onChange={(e) => {
              setIsEmailValid(validateEmailFormat(e.target.value))
            }}
            data-testid={"email-input"}
            disabled={isPending}
            className={`${
              isPending ? "pointer-events-none opacity-50" : ""
            } w-full rounded-md border border-[#e3e3e3] p-[10px] focus:outline-none`}
            placeholder={"email@example.com"}
          />
          {!!emailTypoCorrectionSuggestion && (
            <button
              onClick={() => {
                if (emailInputRef.current) {
                  emailInputRef.current.value = emailTypoCorrectionSuggestion
                  setIsEmailValid(
                    validateEmailFormat(emailTypoCorrectionSuggestion),
                  )
                }
              }}
              type={"button"}
              data-testid={"EMAIL_TYPO_SUGGESTION_TEXT"}
              className={"mt-2 text-center text-sm text-indigo-600"}
            >
              Did you mean{" "}
              <span className={"font-medium"}>
                {emailTypoCorrectionSuggestion.replace(/.+(?=@)/, "")}
              </span>
              ?
            </button>
          )}
          {isEmailInvalid && (
            <p
              data-testid={"EMAIL_VALIDATION_INVALID_TEXT"}
              className={"text-center text-sm text-red-400"}
            >
              Not a valid email
            </p>
          )}
        </div>
        <button
          data-testid={"email-button"}
          formNoValidate={true}
          disabled={isPending || !isEmailValid}
          className={`${isPending ? "pointer-events-none opacity-50" : ""} ${
            isEmailValid
              ? "bg-indigo-600 text-white shadow-sm"
              : "pointer-events-none bg-[#838383] text-white shadow-sm"
          } h-[46px] rounded-md p-[10px] text-sm font-[600] focus:outline-none`}
        >
          {isPending ? (
            <div className={"relative w-full"}>
              <span>Loading...</span>
              <div role="status" className={"absolute right-0 top-0"}>
                <AuthLoadingSpinner />
              </div>
            </div>
          ) : (
            "Continue"
          )}
        </button>
        {error && <ErrorMessage message={error.message} />}
      </form>

      {(isSignInWithGoogleVisible || isSignInWithAppleVisible) && (
        <div className={"flex flex-1 flex-col items-center gap-3 pt-11"}>
          <p
            className={
              "px-4 text-center text-[16px] font-medium text-[#979797]"
            }
          >
            Or add email from
          </p>

          <div
            className={
              "flex h-10 max-h-10 min-h-10 flex-row justify-center gap-x-2 overflow-hidden [&_*]:!outline-none"
            }
          >
            {isSignInWithGoogleVisible && (
              <button
                type="button"
                onClick={googleSignInButtonOnClick}
                className={
                  "relative flex max-h-10 min-h-10 w-28 items-center justify-center overflow-hidden rounded-full ring-1 ring-inset ring-gray-300/90 transition-colors active:bg-sky-100 active:ring-sky-400"
                }
              >
                {isGoogleAuthLoading ? (
                  <AuthLoadingSpinner />
                ) : (
                  <AuthGoogleIcon className={"size-[22px]"} />
                )}
              </button>
            )}
            {isSignInWithAppleVisible && (
              <button
                type="button"
                ref={appleSignInButtonRef}
                onClick={appleSignInButtonOnClick}
                className={
                  "relative flex max-h-10 min-h-10 w-28 items-center justify-center overflow-hidden rounded-full ring-1 ring-inset ring-gray-300/90 transition-colors active:bg-sky-100 active:ring-sky-400 [&>svg]:text-black"
                }
              >
                <AppleIcon size={28} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Email Code Input Screen Component
 * Displays the form for entering the email verification code
 */
interface EmailCodeInputScreenProps {
  email: string
  nonce: string
}

function EmailCodeInputScreen({ email, nonce }: EmailCodeInputScreenProps) {
  const { auth, ...functions } = useFirebase()
  const [isEmailCodeValid, setIsEmailCodeValid] = useState(false)
  const [isProcessingEmailCode, setIsProcessingEmailCode] = useState(false)
  const [error, setError] = useState<AuthFlowError | undefined>(undefined)
  const emailCodeRef = useRef<HTMLInputElement>(null)

  // Handle errors consistently
  const handleError = (message: string, source?: string) => {
    const err = new Error(message)
    console.error({ message, source, error: err })
    Bugsnag.notify(err)
    const errorObj = { message, source }
    setError(errorObj)
    setIsProcessingEmailCode(false)
  }

  // Reset error state
  const clearError = () => {
    setError(undefined)
  }

  // Handle email code submission
  const handleEmailCodeSubmit = async (code: string) => {
    setIsProcessingEmailCode(true)
    clearError()

    try {
      const { token } = await Functions.auth_loginWithEmail(functions, {
        email,
        code,
        nonce,
      })

      // Notify parent to sign in with the token
      await signInWithCustomToken(auth, token)
    } catch (error) {
      handleError((error as Error).message, "fetchIdTokenForEmailCode")
    }
  }

  const submitEmailCode = async () => {
    const emailCodeInput = document.getElementById(
      "emailCode",
    ) as HTMLInputElement
    await handleEmailCodeSubmit(emailCodeInput.value)
  }

  // Focus email code input when component mounts
  useEffect(() => {
    if (emailCodeRef.current) {
      emailCodeRef.current.focus()
    }
  }, [])

  return (
    <form
      className={"flex w-full flex-col gap-[15px]"}
      id={"codeForm"}
      autoComplete={"off"}
      onSubmit={(e) => {
        e.preventDefault()
        if (isEmailCodeValid) {
          submitEmailCode()
        }
      }}
    >
      <h1 className={"mb-[10px] text-lg font-bold"}>
        Enter code sent to your email
      </h1>
      <div
        className={
          "flex flex-row justify-center rounded-md border-[1px] border-[#e3e3e3]"
        }
      >
        <input
          data-testid={"email-code"}
          ref={emailCodeRef}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={"one-time-code"}
          maxLength={4}
          type="text"
          id={"emailCode"}
          className={`${
            isProcessingEmailCode ? "pointer-events-none opacity-50" : ""
          } w-full overflow-visible border-none bg-transparent pl-[50px] pr-[10px] text-center text-[40px] tracking-[40px] shadow-none outline-none ring-0 placeholder:font-[300] placeholder:text-gray-400 focus:border-none focus:shadow-none focus:outline-none focus:ring-0`}
          placeholder={"____"}
          onChange={(e) => {
            const value = e.target.value
            const isValid = value.length >= 4
            setIsEmailCodeValid(isValid)

            if (value.length >= 4) {
              e.target.blur()
            }

            if (isValid) {
              submitEmailCode()
            }
          }}
          disabled={isProcessingEmailCode}
        />
      </div>
      <button
        disabled={isProcessingEmailCode}
        className={`${
          isProcessingEmailCode ? "pointer-events-none opacity-50" : ""
        } ${
          isEmailCodeValid
            ? "bg-indigo-600"
            : "pointer-events-none bg-[#838383]"
        } h-[46px] rounded-md p-[10px] text-sm font-[600] text-white shadow-sm focus:outline-none`}
      >
        {isProcessingEmailCode ? (
          <div className={"relative w-full"}>
            <span>Loading...</span>
            <div role="status" className={"absolute right-0 top-0"}>
              <AuthLoadingSpinner />
            </div>
          </div>
        ) : (
          "Sign in"
        )}
      </button>
      {error && <ErrorMessage message={error.message} />}
    </form>
  )
}

type AuthFlowState =
  | { type: "emailInput" }
  | { type: "emailCode"; email: string; nonce: string }
  | { type: "signedIn" }

/**
 * Main AuthFlow Component
 * Handles the authentication flow using React state management
 */
export default function AuthFlow(props: PropsWithChildren) {
  const { auth } = useFirebase()
  const { loadingUser } = useUser()

  const [authFlowState, setAuthFlowState] = useState<AuthFlowState>({
    type: "emailInput",
  })

  // Refs for DOM elements
  const appleSignInButtonRef = useRef<HTMLButtonElement>(null)

  // Check if browser is supported
  const isUnsupportedBrowser = useIsUnsupportedBrowser()

  // Apple Sign In state
  const [isAppleClientLibLoaded, setIsAppleClientLibLoaded] = useState(false)
  const [isAppleAuthLoading, setIsAppleAuthLoading] = useState(false)

  // Path and device detection
  const pathname = usePathname()
  const [isAndroidPaymentSheet, setIsAndroidPaymentSheet] = useState(false)
  const [isUpdatePrivateRelay, setIsUpdatePrivateRelay] = useState(false)

  // Visibility flags for UI components
  const isSignInWithAppleVisible =
    !loadingUser && !isUnsupportedBrowser && !isAndroidPaymentSheet
  const isSignInWithGoogleVisible =
    !loadingUser && !isUnsupportedBrowser && !isUpdatePrivateRelay
  const isSignInWithEmailVisible = !loadingUser && !isUpdatePrivateRelay
  const isBrowserNotSupportedVisible =
    !loadingUser &&
    !isSignInWithGoogleVisible &&
    !isSignInWithAppleVisible &&
    !isSignInWithEmailVisible

  // Check for Apple ID library
  useInterval(
    () => {
      const windowWithAppleID = window as WindowWithAppleID
      if (windowWithAppleID.AppleID) {
        setIsAppleClientLibLoaded(true)
      }
    },
    isAppleClientLibLoaded ? null : 100,
  )

  /**
   * Authentication Flow Functions
   */

  // Handle email validation success
  const onEmailInputComplete = (value: OnInputInputCompleteValue) => {
    if (value.type === "signedIn") {
      setAuthFlowState({ type: "signedIn" })
      return
    }

    if (value.type === "emailValid") {
      const { email, nonce } = value
      setAuthFlowState({ type: "emailCode", email, nonce })
    }
  }

  // Apple sign in button click handler
  const appleSignInButtonOnClick = () => {
    setIsAppleAuthLoading(true)
    setTimeout(() => setIsAppleAuthLoading(false), 5000)
    setTimeout(() => {
      const windowWithAppleID = window as WindowWithAppleID
      if (windowWithAppleID.AppleID) {
        windowWithAppleID.AppleID.auth.signIn().catch((result: any) => {
          if (result.error !== "popup_closed_by_user") {
            Bugsnag.notify(new Error("Apple signIn error", { cause: result }))
          }
        })
      }
    }, 50)
  }

  /**
   * Effects
   */

  // Process path segments for device detection
  useEffect(() => {
    const pathnameSegments = pathname.slice(1).split("/")
    setIsAndroidPaymentSheet(
      rdd.isAndroid &&
        pathnameSegments.length === 2 &&
        ["offerings", "providers"].includes(pathnameSegments[0]),
    )
    setIsUpdatePrivateRelay(
      pathnameSegments.length === 1 &&
        pathnameSegments[0] === "update_private_relay",
    )
  }, [pathname])

  // Render Apple button when library is loaded
  useEffect(() => {
    if (appleSignInButtonRef.current && isAppleClientLibLoaded) {
      const windowWithAppleID = window as WindowWithAppleID
      if (windowWithAppleID.AppleID) {
        windowWithAppleID.AppleID.auth.renderButton()
      }
    }
  }, [appleSignInButtonRef.current, isAppleClientLibLoaded])

  // Listen for auth state changes
  useEffect(() => {
    // Clean up subscription
    return onAuthStateChanged(auth, (user) => {
      if (user?.email) {
        // User has email, they're signed in
        // If we have just signed them in anonymously
        // There is no email yet, we update the email
        // and then set the auth flow state to signedIn
        setAuthFlowState({ type: "signedIn" })
        return
      }

      if (user === null) {
        // User is signed out, set auth flow state to email input
        setAuthFlowState({ type: "emailInput" })
        return
      }
    })
  }, [auth])

  // If signed in, render children
  if (authFlowState.type === "signedIn") {
    return props.children
  }

  return (
    <>
      <SignInAndPayHeader />
      <div
        id={"signInButtonsContainer"}
        data-testid={"sign-in-sheet"}
        className={"flex flex-1 flex-col gap-3"}
      >
        {authFlowState.type === "emailInput" && (
          <>
            {isBrowserNotSupportedVisible && <UnsupportedBrowserMessage />}
            {isSignInWithEmailVisible && (
              <EmailInputScreen
                onComplete={onEmailInputComplete}
                isSignInWithGoogleVisible={isSignInWithGoogleVisible}
                isSignInWithAppleVisible={isSignInWithAppleVisible}
                appleSignInButtonRef={appleSignInButtonRef}
                appleSignInButtonOnClick={appleSignInButtonOnClick}
              />
            )}
          </>
        )}

        {authFlowState.type === "emailCode" && (
          <EmailCodeInputScreen
            email={authFlowState.email}
            nonce={authFlowState.nonce}
          />
        )}
      </div>
    </>
  )
}
